import type {TFootprintResponse, TFootprintStatus} from "api/polarisApi/types";
import {errorCodeOf} from "./thunkUtils";

// The footprint's generation status. An older backend omits the field and the
// flag-off backend always says "ready" — both must behave exactly as before,
// so absent reads as "ready". A footprint that has not loaded yet also reads
// as "ready" (the legacy empty-footprint handling then applies).
export const footprintStatusOf = (
	footprint: Pick<TFootprintResponse, "status"> | null | undefined,
): TFootprintStatus => footprint?.status ?? "ready";

// pending / generating: the map is still being built, clusters and companies
// may still arrive, and edits / confirm are refused by the backend (409).
export const isFootprintGenerating = (
	footprint: Pick<TFootprintResponse, "status"> | null | undefined,
): boolean => {
	const status = footprintStatusOf(footprint);

	return status === "pending" || status === "generating";
};

// 409 codes meaning "the footprint is not in the state this UI assumed". They
// call for a silent refresh (the refreshed state then explains itself:
// progress line, failed banner) rather than an error message.
//   footprint_generating    edit / confirm while pending or generating
//   footprint_failed        edit / confirm after generation failed
//   footprint_not_retryable retry when the backend no longer allows it
export const FOOTPRINT_STATE_CONFLICT_CODES: ReadonlySet<string> = new Set([
	"footprint_generating",
	"footprint_failed",
	"footprint_not_retryable",
]);

export const isFootprintStateConflict = (error: unknown): boolean => {
	const code = errorCodeOf(error);

	return code !== undefined && FOOTPRINT_STATE_CONFLICT_CODES.has(code);
};
